import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  LayoutDashboard, Users, Package, ShoppingCart, FileText, LogOut, Menu, X, 
  Plus, Edit, Trash2, Eye, Upload, Image as ImageIcon, CheckCircle, XCircle,
  DollarSign, TrendingUp, AlertCircle, Search, Filter, RefreshCw, Award, Wallet,
  Building, Car, Utensils, ShoppingBag, Zap, ToggleLeft, ToggleRight
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Admin Login Component
const AdminLogin = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/admin/login`, { email, password });
      localStorage.setItem('adminToken', response.data.token);
      localStorage.setItem('adminData', JSON.stringify(response.data));
      onLogin(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-light tracking-tight">Perfect Circle</CardTitle>
          <CardDescription>Admin Dashboard</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@example.com"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
              />
            </div>
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// Dashboard Overview Component
const DashboardOverview = ({ analytics, loading }) => {
  if (loading) {
    return <div className="p-4 text-center">Loading analytics...</div>;
  }

  const stats = [
    { label: 'Total Users', value: analytics?.totals?.users || 0, icon: Users, color: 'text-blue-600' },
    { label: 'Total Sellers', value: analytics?.totals?.sellers || 0, icon: Users, color: 'text-purple-600' },
    { label: 'Products', value: analytics?.totals?.products || 0, icon: Package, color: 'text-green-600' },
    { label: 'Orders', value: analytics?.totals?.orders || 0, icon: ShoppingCart, color: 'text-orange-600' },
    { label: 'Pending Requests', value: analytics?.totals?.pending_requests || 0, icon: FileText, color: 'text-red-600' },
    { label: 'Revenue', value: `£${(analytics?.totals?.revenue || 0).toLocaleString()}`, icon: DollarSign, color: 'text-emerald-600' },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {stats.map((stat) => (
          <Card key={stat.label}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <stat.icon className={`h-5 w-5 ${stat.color}`} />
                <span className="text-2xl font-semibold">{stat.value}</span>
              </div>
              <p className="text-xs text-gray-500 mt-2">{stat.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Recent Users</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {analytics?.recent_activity?.users?.map((user, idx) => (
                <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <div>
                    <p className="font-medium text-sm">{user.name}</p>
                    <p className="text-xs text-gray-500">{user.email}</p>
                  </div>
                </div>
              ))}
              {(!analytics?.recent_activity?.users || analytics.recent_activity.users.length === 0) && (
                <p className="text-sm text-gray-500">No recent users</p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Recent Orders</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {analytics?.recent_activity?.orders?.map((order, idx) => (
                <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <div>
                    <p className="font-medium text-sm">Order #{order.id?.slice(0, 8)}</p>
                    <p className="text-xs text-gray-500">£{order.total_amount}</p>
                  </div>
                  <Badge variant={order.order_status === 'completed' ? 'default' : 'secondary'}>
                    {order.order_status}
                  </Badge>
                </div>
              ))}
              {(!analytics?.recent_activity?.orders || analytics.recent_activity.orders.length === 0) && (
                <p className="text-sm text-gray-500">No recent orders</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Products Management Component
const ProductsManager = ({ token }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    name: '', brand: '', category: 'bags', price: '', description: '',
    image_url: '', condition: 'Excellent', authenticity_certificate: ''
  });
  const [imageFiles, setImageFiles] = useState([]);
  const [certificateFile, setCertificateFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/admin/products`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(response.data.products || []);
    } catch (err) {
      console.error('Failed to fetch products:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProducts(); }, [token]);

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImageFiles(prev => [...prev, { file, preview: reader.result, base64: reader.result }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleCertificateUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setCertificateFile({ file, preview: reader.result, base64: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = (index) => {
    setImageFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);
    
    try {
      const productData = {
        ...formData,
        price: parseFloat(formData.price),
        water_saved_litres: parseFloat(formData.price) * 200,
        co2_saved_litres: parseFloat(formData.price) * 3,
        image_url: imageFiles.length > 0 ? imageFiles[0].base64 : formData.image_url,
        additional_images: imageFiles.slice(1).map(img => img.base64),
        authenticity_certificate: certificateFile?.base64 || formData.authenticity_certificate
      };

      if (editingProduct) {
        await axios.put(`${API}/admin/products/${editingProduct.id}`, productData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API}/admin/products`, productData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      fetchProducts();
      resetForm();
    } catch (err) {
      console.error('Failed to save product:', err);
      alert('Failed to save product: ' + (err.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', brand: '', category: 'bags', price: '', description: '', image_url: '', condition: 'Excellent', authenticity_certificate: '' });
    setImageFiles([]);
    setCertificateFile(null);
    setEditingProduct(null);
    setShowForm(false);
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      brand: product.brand,
      category: product.category,
      price: product.price.toString(),
      description: product.description,
      image_url: product.image_url,
      condition: product.condition || 'Excellent',
      authenticity_certificate: product.authenticity_certificate || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await axios.delete(`${API}/admin/products/${productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchProducts();
    } catch (err) {
      console.error('Failed to delete product:', err);
    }
  };

  const filteredProducts = products.filter(p => 
    p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.brand?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search products..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Button onClick={() => setShowForm(true)}>
          <Plus className="h-4 w-4 mr-2" /> Add Product
        </Button>
      </div>

      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle>{editingProduct ? 'Edit Product' : 'Add New Product'}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Product Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Brand *</Label>
                  <Input
                    value={formData.brand}
                    onChange={(e) => setFormData({...formData, brand: e.target.value})}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Category *</Label>
                  <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bags">Bags</SelectItem>
                      <SelectItem value="watches">Watches</SelectItem>
                      <SelectItem value="accessories">Accessories</SelectItem>
                      <SelectItem value="jewelry">Jewelry</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Condition *</Label>
                  <Select value={formData.condition} onValueChange={(v) => setFormData({...formData, condition: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="New">New</SelectItem>
                      <SelectItem value="Excellent">Excellent</SelectItem>
                      <SelectItem value="Very Good">Very Good</SelectItem>
                      <SelectItem value="Good">Good</SelectItem>
                      <SelectItem value="Fair">Fair</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Price (GBP) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({...formData, price: e.target.value})}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Description *</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows={3}
                  required
                />
              </div>

              {/* Product Images Upload */}
              <div className="space-y-2">
                <Label>Product Images</Label>
                <div className="border-2 border-dashed rounded-lg p-4">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageUpload}
                    className="hidden"
                    id="product-images"
                  />
                  <label htmlFor="product-images" className="cursor-pointer flex flex-col items-center">
                    <Upload className="h-8 w-8 text-gray-400 mb-2" />
                    <span className="text-sm text-gray-500">Click to upload images</span>
                  </label>
                </div>
                {imageFiles.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {imageFiles.map((img, idx) => (
                      <div key={idx} className="relative">
                        <img src={img.preview} alt="preview" className="w-20 h-20 object-cover rounded" />
                        <button
                          type="button"
                          onClick={() => removeImage(idx)}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-xs text-gray-500">Or enter image URL:</p>
                <Input
                  value={formData.image_url}
                  onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                  placeholder="https://example.com/image.jpg"
                />
              </div>

              {/* Authenticity Certificate Upload */}
              <div className="space-y-2">
                <Label>Authenticity Certificate</Label>
                <div className="border-2 border-dashed rounded-lg p-4">
                  <input
                    type="file"
                    accept="image/*,.pdf"
                    onChange={handleCertificateUpload}
                    className="hidden"
                    id="certificate"
                  />
                  <label htmlFor="certificate" className="cursor-pointer flex flex-col items-center">
                    <Award className="h-8 w-8 text-gray-400 mb-2" />
                    <span className="text-sm text-gray-500">Upload authenticity certificate</span>
                  </label>
                </div>
                {certificateFile && (
                  <div className="flex items-center gap-2 p-2 bg-green-50 rounded">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="text-sm text-green-700">Certificate uploaded</span>
                    <button type="button" onClick={() => setCertificateFile(null)} className="ml-auto text-red-500">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>

              <div className="flex gap-2">
                <Button type="submit" disabled={uploading}>
                  {uploading ? 'Saving...' : (editingProduct ? 'Update Product' : 'Create Product')}
                </Button>
                <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <p className="text-center py-8">Loading products...</p>
      ) : (
        <Card>
          <ScrollArea className="h-[500px]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Image</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Brand</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead>Condition</TableHead>
                  <TableHead>Certificate</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredProducts.map((product) => (
                  <TableRow key={product.id}>
                    <TableCell>
                      <img src={product.image_url} alt={product.name} className="w-12 h-12 object-cover rounded" />
                    </TableCell>
                    <TableCell className="font-medium">{product.name}</TableCell>
                    <TableCell>{product.brand}</TableCell>
                    <TableCell><Badge variant="outline">{product.category}</Badge></TableCell>
                    <TableCell>£{product.price}</TableCell>
                    <TableCell>{product.condition}</TableCell>
                    <TableCell>
                      {product.authenticity_certificate ? (
                        <CheckCircle className="h-4 w-4 text-green-600" />
                      ) : (
                        <XCircle className="h-4 w-4 text-gray-300" />
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button size="sm" variant="ghost" onClick={() => handleEdit(product)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDelete(product.id)}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollArea>
        </Card>
      )}
    </div>
  );
};

// Users Management Component
const UsersManager = ({ token }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data.users || []);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, [token]);

  const toggleStatus = async (userId) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/toggle-status`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchUsers();
    } catch (err) {
      console.error('Failed to toggle user status:', err);
    }
  };

  const filteredUsers = users.filter(u =>
    u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-4">
      <div className="relative w-full sm:w-64">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          placeholder="Search users..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {loading ? (
        <p className="text-center py-8">Loading users...</p>
      ) : (
        <Card>
          <ScrollArea className="h-[500px]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Wallet Points</TableHead>
                  <TableHead>Orders</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell className="font-medium">{user.name}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.phone}</TableCell>
                    <TableCell>{user.wallet_points || 0}</TableCell>
                    <TableCell>{user.total_orders || 0}</TableCell>
                    <TableCell>
                      <Badge variant={user.status === 'active' ? 'default' : 'secondary'}>
                        {user.status || 'active'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Button size="sm" variant="outline" onClick={() => toggleStatus(user.id)}>
                        {user.status === 'active' ? 'Deactivate' : 'Activate'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollArea>
        </Card>
      )}
    </div>
  );
};

// Sellers Management Component
const SellersManager = ({ token }) => {
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchSellers = async () => {
    try {
      const response = await axios.get(`${API}/admin/sellers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSellers(response.data.sellers || []);
    } catch (err) {
      console.error('Failed to fetch sellers:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSellers(); }, [token]);

  const updateStatus = async (sellerId, status) => {
    try {
      await axios.put(`${API}/admin/sellers/${sellerId}/status?status=${status}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchSellers();
    } catch (err) {
      console.error('Failed to update seller status:', err);
    }
  };

  const filteredSellers = sellers.filter(s =>
    s.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.business_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-4">
      <div className="relative w-full sm:w-64">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          placeholder="Search sellers..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {loading ? (
        <p className="text-center py-8">Loading sellers...</p>
      ) : (
        <Card>
          <ScrollArea className="h-[500px]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Business</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Earnings</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredSellers.map((seller) => (
                  <TableRow key={seller.id}>
                    <TableCell className="font-medium">{seller.name}</TableCell>
                    <TableCell>{seller.business_name || '-'}</TableCell>
                    <TableCell>{seller.email}</TableCell>
                    <TableCell>£{seller.total_earnings || 0}</TableCell>
                    <TableCell>
                      <Badge variant={seller.status === 'active' ? 'default' : seller.status === 'suspended' ? 'destructive' : 'secondary'}>
                        {seller.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Select
                        value={seller.status}
                        onValueChange={(value) => updateStatus(seller.id, value)}
                      >
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="suspended">Suspended</SelectItem>
                          <SelectItem value="pending_approval">Pending</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollArea>
        </Card>
      )}
    </div>
  );
};

// Orders Management Component
const OrdersManager = ({ token }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/admin/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data.orders || []);
    } catch (err) {
      console.error('Failed to fetch orders:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOrders(); }, [token]);

  const updateOrder = async (orderId, status) => {
    try {
      await axios.put(`${API}/admin/orders/${orderId}`, { order_status: status }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchOrders();
    } catch (err) {
      console.error('Failed to update order:', err);
    }
  };

  return (
    <div className="space-y-4">
      <div className="relative w-full sm:w-64">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          placeholder="Search orders..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {loading ? (
        <p className="text-center py-8">Loading orders...</p>
      ) : (
        <Card>
          <ScrollArea className="h-[500px]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Order ID</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Payment</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orders.map((order) => (
                  <TableRow key={order.id}>
                    <TableCell className="font-mono text-sm">{order.id?.slice(0, 8)}...</TableCell>
                    <TableCell>{order.user_email}</TableCell>
                    <TableCell>£{order.total_amount}</TableCell>
                    <TableCell>
                      <Badge variant={order.payment_status === 'completed' ? 'default' : 'secondary'}>
                        {order.payment_status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={order.order_status === 'delivered' ? 'default' : order.order_status === 'cancelled' ? 'destructive' : 'secondary'}>
                        {order.order_status}
                      </Badge>
                    </TableCell>
                    <TableCell>{new Date(order.created_at).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <Select
                        value={order.order_status}
                        onValueChange={(value) => updateOrder(order.id, value)}
                      >
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="processing">Processing</SelectItem>
                          <SelectItem value="shipped">Shipped</SelectItem>
                          <SelectItem value="delivered">Delivered</SelectItem>
                          <SelectItem value="cancelled">Cancelled</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollArea>
        </Card>
      )}
    </div>
  );
};

// Sell Requests Management Component
const SellRequestsManager = ({ token }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchRequests = async () => {
    try {
      const response = await axios.get(`${API}/admin/sell-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequests(response.data.requests || []);
    } catch (err) {
      console.error('Failed to fetch sell requests:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRequests(); }, [token]);

  const updateStatus = async (requestId, status) => {
    try {
      await axios.put(`${API}/admin/sell-requests/${requestId}/status?status=${status}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchRequests();
    } catch (err) {
      console.error('Failed to update request status:', err);
    }
  };

  const filteredRequests = requests.filter(r =>
    r.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    r.brand?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    r.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-4">
      <div className="relative w-full sm:w-64">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          placeholder="Search requests..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {loading ? (
        <p className="text-center py-8">Loading sell requests...</p>
      ) : (
        <Card>
          <ScrollArea className="h-[500px]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Item</TableHead>
                  <TableHead>Brand</TableHead>
                  <TableHead>Seller</TableHead>
                  <TableHead>Condition</TableHead>
                  <TableHead>Est. Value</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRequests.map((request) => (
                  <TableRow key={request.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {request.images?.[0] && (
                          <img src={request.images[0]} alt="" className="w-10 h-10 object-cover rounded" />
                        )}
                        <span className="font-medium">{request.item_type}</span>
                      </div>
                    </TableCell>
                    <TableCell>{request.brand}</TableCell>
                    <TableCell>
                      <div>
                        <p className="text-sm">{request.name}</p>
                        <p className="text-xs text-gray-500">{request.email}</p>
                      </div>
                    </TableCell>
                    <TableCell>{request.condition}</TableCell>
                    <TableCell>£{request.estimated_value || '-'}</TableCell>
                    <TableCell>
                      <Badge variant={
                        request.status === 'accepted' ? 'default' : 
                        request.status === 'rejected' ? 'destructive' : 'secondary'
                      }>
                        {request.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button size="sm" variant="ghost" onClick={() => setSelectedRequest(request)}>
                              <Eye className="h-4 w-4" />
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                            <DialogHeader>
                              <DialogTitle>Sell Request Details</DialogTitle>
                            </DialogHeader>
                            {selectedRequest && (
                              <div className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                  <div>
                                    <Label className="text-gray-500">Item Type</Label>
                                    <p className="font-medium">{selectedRequest.item_type}</p>
                                  </div>
                                  <div>
                                    <Label className="text-gray-500">Brand</Label>
                                    <p className="font-medium">{selectedRequest.brand}</p>
                                  </div>
                                  <div>
                                    <Label className="text-gray-500">Model</Label>
                                    <p className="font-medium">{selectedRequest.model || '-'}</p>
                                  </div>
                                  <div>
                                    <Label className="text-gray-500">Condition</Label>
                                    <p className="font-medium">{selectedRequest.condition}</p>
                                  </div>
                                  <div>
                                    <Label className="text-gray-500">Estimated Value</Label>
                                    <p className="font-medium">£{selectedRequest.estimated_value || '-'}</p>
                                  </div>
                                  <div>
                                    <Label className="text-gray-500">Has Authenticity Cards</Label>
                                    <p className="font-medium">{selectedRequest.authenticity_cards ? 'Yes' : 'No'}</p>
                                  </div>
                                </div>
                                <div>
                                  <Label className="text-gray-500">Description</Label>
                                  <p>{selectedRequest.description}</p>
                                </div>
                                <div>
                                  <Label className="text-gray-500">Seller Contact</Label>
                                  <p>{selectedRequest.name} - {selectedRequest.email}</p>
                                  <p>{selectedRequest.phone}</p>
                                </div>
                                {selectedRequest.images?.length > 0 && (
                                  <div>
                                    <Label className="text-gray-500">Images</Label>
                                    <div className="grid grid-cols-4 gap-2 mt-2">
                                      {selectedRequest.images.map((img, idx) => (
                                        <img key={idx} src={img} alt={`Item ${idx + 1}`} className="w-full aspect-square object-cover rounded" />
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </DialogContent>
                        </Dialog>
                        <Select
                          value={request.status}
                          onValueChange={(value) => updateStatus(request.id, value)}
                        >
                          <SelectTrigger className="w-28">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="reviewing">Reviewing</SelectItem>
                            <SelectItem value="quoted">Quoted</SelectItem>
                            <SelectItem value="accepted">Accepted</SelectItem>
                            <SelectItem value="rejected">Rejected</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollArea>
        </Card>
      )}
    </div>
  );
};

// Wallet Partners Management Component
const WalletPartnersManager = ({ token }) => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    name: '', category: 'transportation', description: '',
    redemption_rate: '', min_redemption: '0', logo_url: ''
  });
  const [logoFile, setLogoFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const categories = [
    { value: 'transportation', label: 'Transportation', icon: Car },
    { value: 'food_delivery', label: 'Food & Delivery', icon: Utensils },
    { value: 'shopping', label: 'Shopping', icon: ShoppingBag },
    { value: 'entertainment', label: 'Entertainment', icon: Zap },
    { value: 'lifestyle', label: 'Lifestyle', icon: Building },
  ];

  const fetchPartners = async () => {
    try {
      const response = await axios.get(`${API}/admin/wallet-partners`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPartners(response.data.partners || []);
    } catch (err) {
      console.error('Failed to fetch partners:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPartners(); }, [token]);

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoFile({ file, preview: reader.result, base64: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const partnerData = {
        name: formData.name,
        category: formData.category,
        description: formData.description,
        redemption_rate: parseFloat(formData.redemption_rate),
        min_redemption: parseFloat(formData.min_redemption) || 0,
        logo_url: logoFile?.base64 || formData.logo_url || null
      };

      if (editingPartner) {
        await axios.put(`${API}/admin/wallet-partners/${editingPartner.id}`, partnerData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API}/admin/wallet-partners`, partnerData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      fetchPartners();
      resetForm();
    } catch (err) {
      console.error('Failed to save partner:', err);
      alert('Failed to save partner: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', category: 'transportation', description: '', redemption_rate: '', min_redemption: '0', logo_url: '' });
    setLogoFile(null);
    setEditingPartner(null);
    setShowForm(false);
  };

  const handleEdit = (partner) => {
    setEditingPartner(partner);
    setFormData({
      name: partner.name,
      category: partner.category,
      description: partner.description,
      redemption_rate: partner.redemption_rate.toString(),
      min_redemption: (partner.min_redemption || 0).toString(),
      logo_url: partner.logo_url || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (partnerId) => {
    if (!window.confirm('Are you sure you want to delete this partner?')) return;
    try {
      await axios.delete(`${API}/admin/wallet-partners/${partnerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchPartners();
    } catch (err) {
      console.error('Failed to delete partner:', err);
    }
  };

  const toggleActive = async (partner) => {
    try {
      await axios.put(`${API}/admin/wallet-partners/${partner.id}`, 
        { is_active: !partner.is_active },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchPartners();
    } catch (err) {
      console.error('Failed to toggle partner status:', err);
    }
  };

  const filteredPartners = partners.filter(p =>
    p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.category?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getCategoryIcon = (category) => {
    const cat = categories.find(c => c.value === category);
    return cat ? cat.icon : Building;
  };

  const getCategoryLabel = (category) => {
    const cat = categories.find(c => c.value === category);
    return cat ? cat.label : category;
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search partners..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Button onClick={() => setShowForm(true)}>
          <Plus className="h-4 w-4 mr-2" /> Add Partner
        </Button>
      </div>

      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle>{editingPartner ? 'Edit Partner' : 'Add New Wallet Partner'}</CardTitle>
            <CardDescription>Partners allow users to redeem their wallet points for rewards</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Partner Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="e.g., Uber Green"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Category *</Label>
                  <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Redemption Rate (points per £1) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.redemption_rate}
                    onChange={(e) => setFormData({...formData, redemption_rate: e.target.value})}
                    placeholder="e.g., 100 (100 points = £1)"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Minimum Points for Redemption</Label>
                  <Input
                    type="number"
                    value={formData.min_redemption}
                    onChange={(e) => setFormData({...formData, min_redemption: e.target.value})}
                    placeholder="e.g., 500"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Description *</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Describe what users can redeem with this partner"
                  rows={3}
                  required
                />
              </div>

              {/* Logo Upload */}
              <div className="space-y-2">
                <Label>Partner Logo</Label>
                <div className="flex items-start gap-4">
                  <div className="border-2 border-dashed rounded-lg p-4 flex-1">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="hidden"
                      id="partner-logo"
                    />
                    <label htmlFor="partner-logo" className="cursor-pointer flex flex-col items-center">
                      <Upload className="h-8 w-8 text-gray-400 mb-2" />
                      <span className="text-sm text-gray-500">Upload logo image</span>
                    </label>
                  </div>
                  {(logoFile || formData.logo_url) && (
                    <div className="w-20 h-20 border rounded overflow-hidden">
                      <img 
                        src={logoFile?.preview || formData.logo_url} 
                        alt="Logo preview" 
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-500">Or enter logo URL:</p>
                <Input
                  value={formData.logo_url}
                  onChange={(e) => setFormData({...formData, logo_url: e.target.value})}
                  placeholder="https://example.com/logo.png"
                />
              </div>

              <div className="flex gap-2">
                <Button type="submit" disabled={saving}>
                  {saving ? 'Saving...' : (editingPartner ? 'Update Partner' : 'Create Partner')}
                </Button>
                <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <p className="text-center py-8">Loading wallet partners...</p>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredPartners.map((partner) => {
            const CategoryIcon = getCategoryIcon(partner.category);
            return (
              <Card key={partner.id} className={!partner.is_active ? 'opacity-60' : ''}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      {partner.logo_url ? (
                        <img src={partner.logo_url} alt={partner.name} className="w-12 h-12 rounded-lg object-cover" />
                      ) : (
                        <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                          <CategoryIcon className="h-6 w-6 text-gray-400" />
                        </div>
                      )}
                      <div>
                        <h3 className="font-semibold">{partner.name}</h3>
                        <Badge variant="outline" className="text-xs">
                          {getCategoryLabel(partner.category)}
                        </Badge>
                      </div>
                    </div>
                    <button 
                      onClick={() => toggleActive(partner)}
                      className="text-gray-400 hover:text-gray-600"
                      title={partner.is_active ? 'Deactivate' : 'Activate'}
                    >
                      {partner.is_active ? (
                        <ToggleRight className="h-6 w-6 text-green-500" />
                      ) : (
                        <ToggleLeft className="h-6 w-6 text-gray-400" />
                      )}
                    </button>
                  </div>
                  
                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">{partner.description}</p>
                  
                  <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                    <div className="bg-gray-50 rounded p-2">
                      <p className="text-gray-500 text-xs">Rate</p>
                      <p className="font-medium">{partner.redemption_rate} pts/£1</p>
                    </div>
                    <div className="bg-gray-50 rounded p-2">
                      <p className="text-gray-500 text-xs">Min Points</p>
                      <p className="font-medium">{partner.min_redemption || 0}</p>
                    </div>
                  </div>

                  <div className="flex justify-end gap-2">
                    <Button size="sm" variant="ghost" onClick={() => handleEdit(partner)}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(partner.id)}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
          
          {filteredPartners.length === 0 && !loading && (
            <div className="col-span-full text-center py-8 text-gray-500">
              No wallet partners found. Add your first partner to get started.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Main Admin Dashboard Component
const AdminApp = () => {
  const [admin, setAdmin] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    const adminData = localStorage.getItem('adminData');
    if (token && adminData) {
      setAdmin(JSON.parse(adminData));
    }
  }, []);

  useEffect(() => {
    if (admin?.token) {
      fetchAnalytics();
    }
  }, [admin]);

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/admin/analytics`, {
        headers: { Authorization: `Bearer ${admin.token}` }
      });
      setAnalytics(response.data);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminData');
    setAdmin(null);
  };

  if (!admin) {
    return <AdminLogin onLogin={setAdmin} />;
  }

  const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'products', label: 'Products', icon: Package },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'sellers', label: 'Sellers', icon: Users },
    { id: 'orders', label: 'Orders', icon: ShoppingCart },
    { id: 'sell-requests', label: 'Sell Requests', icon: FileText },
    { id: 'wallet-partners', label: 'Wallet Partners', icon: Wallet },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 bg-white border-b z-50 px-4 py-3 flex items-center justify-between">
        <button onClick={() => setSidebarOpen(!sidebarOpen)}>
          {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
        <span className="font-semibold">Admin Dashboard</span>
        <button onClick={handleLogout}>
          <LogOut className="h-5 w-5" />
        </button>
      </div>

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 h-full bg-white border-r z-40 transition-transform duration-200
        lg:translate-x-0 lg:w-64 w-64
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="p-6 border-b hidden lg:block">
          <h1 className="text-xl font-semibold tracking-tight">Perfect Circle</h1>
          <p className="text-sm text-gray-500">Admin Dashboard</p>
        </div>
        <div className="lg:hidden h-14" /> {/* Spacer for mobile header */}
        
        <nav className="p-4 space-y-1">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => {
                setActiveTab(item.id);
                setSidebarOpen(false);
              }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm transition-colors
                ${activeTab === item.id ? 'bg-black text-white' : 'text-gray-600 hover:bg-gray-100'}
              `}
            >
              <item.icon className="h-5 w-5" />
              {item.label}
            </button>
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t hidden lg:block">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">{admin.name}</p>
              <p className="text-xs text-gray-500">{admin.email}</p>
            </div>
            <button onClick={handleLogout} className="p-2 hover:bg-gray-100 rounded">
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 lg:hidden" 
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="lg:ml-64 pt-14 lg:pt-0">
        <div className="p-4 lg:p-8">
          <div className="mb-6">
            <h2 className="text-2xl font-semibold capitalize">
              {activeTab.replace('-', ' ')}
            </h2>
            {activeTab === 'dashboard' && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={fetchAnalytics}
                className="mt-2"
              >
                <RefreshCw className="h-4 w-4 mr-2" /> Refresh
              </Button>
            )}
          </div>

          {activeTab === 'dashboard' && (
            <DashboardOverview analytics={analytics} loading={analyticsLoading} />
          )}
          {activeTab === 'products' && <ProductsManager token={admin.token} />}
          {activeTab === 'users' && <UsersManager token={admin.token} />}
          {activeTab === 'sellers' && <SellersManager token={admin.token} />}
          {activeTab === 'orders' && <OrdersManager token={admin.token} />}
          {activeTab === 'sell-requests' && <SellRequestsManager token={admin.token} />}
          {activeTab === 'wallet-partners' && <WalletPartnersManager token={admin.token} />}
        </div>
      </main>
    </div>
  );
};

export default AdminApp;
